import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Activity, Pause, Play } from "lucide-react";
import { getCombinedPortfolio } from "../lib/api";

/**
 * Bloomberg-style horizontal scrolling ticker banner.
 *
 * Shows every holding with: symbol · CMP · day-change %.
 * Updates every 30s during market hours, 5min outside.
 * Loops infinitely via duplicated track + CSS keyframes.
 *
 * Interactions:
 *  - Hover anywhere on the band → pause scroll
 *  - Click an item → open Stock Detail page (via onStockClick)
 *  - Pause/Play toggle on the right
 */
function buildTickerItems(holdings) {
  const seen = new Set();
  const list = [];
  for (const h of holdings || []) {
    const sym = (h.tradingsymbol || "").toUpperCase();
    const country = h.country || "India";
    const k = `${sym}|${country}`;
    if (!sym || seen.has(k)) continue;
    if (h.asset_class === "Mutual Fund" || h.broker === "mutual_fund") continue;

    // A failed quote refresh must not remove the whole ticker. Prefer the
    // verified same-session quote, then retain the last portfolio price as a
    // clearly labelled cached value. Never reuse an unverified daily move.
    const quotePrice = Number(h.quote_price || h.current_price || h.last_price || 0);
    if (quotePrice <= 0) continue;
    const stale = Boolean(h.quote_stale || !h.quote_at);
    const dayChange = !stale && h.day_change_pct !== null && h.day_change_pct !== undefined
      ? Number(h.day_change_pct)
      : null;

    seen.add(k);
    list.push({
      symbol: sym,
      country,
      last_price: quotePrice,
      day_change_pct: Number.isFinite(dayChange) ? dayChange : null,
      quote_at: h.quote_at,
      quote_source: h.quote_source,
      stale,
    });
  }
  // Verified movers first; cached-price-only entries follow alphabetically.
  list.sort((a, b) => {
    if (a.day_change_pct === null && b.day_change_pct !== null) return 1;
    if (a.day_change_pct !== null && b.day_change_pct === null) return -1;
    if (a.day_change_pct !== null && b.day_change_pct !== null) {
      return Math.abs(b.day_change_pct) - Math.abs(a.day_change_pct);
    }
    return a.symbol.localeCompare(b.symbol);
  });
  return list;
}

export default function TickerTape({ pollMs, onStockClick, fallbackHoldings = [] }) {
  const [items, setItems] = useState([]);
  const [paused, setPaused] = useState(false);
  const [hovered, setHovered] = useState(false);

  const isMarketOpen = useMemo(() => () => {
    const now = new Date();
    const ist = new Date(now.getTime() + (now.getTimezoneOffset() + 330) * 60000);
    const day = ist.getDay();
    if (day === 0 || day === 6) return false;
    const m = ist.getHours() * 60 + ist.getMinutes();
    return m >= (9 * 60 + 15) && m <= (15 * 60 + 30);
  }, []);

  const load = useCallback(async () => {
    try {
      const res = await getCombinedPortfolio();
      const holdings = res?.data?.holdings || [];
      const list = buildTickerItems(holdings);
      setItems(list.length ? list : buildTickerItems(fallbackHoldings));
    } catch (e) {
      console.error("ticker load failed", e);
      setItems(current => current.length ? current : buildTickerItems(fallbackHoldings));
    }
  }, [fallbackHoldings]);

  useEffect(() => {
    load();
    const ms = pollMs ?? (isMarketOpen() ? 30000 : 300000);
    const i = setInterval(load, ms);
    return () => clearInterval(i);
  }, [load, pollMs, isMarketOpen]);

  if (items.length === 0) return null;

  const fmtPrice = (p, c) => c === "US" ? `$${p.toFixed(2)}` : `₹${p.toFixed(2)}`;
  const handleClick = (sym) => { if (onStockClick) onStockClick(sym); };

  const renderItem = (it, idx, copyId) => {
    const hasMove = it.day_change_pct !== null;
    const up = hasMove && it.day_change_pct >= 0;
    const clickable = !!onStockClick;
    return (
      <div
        key={`${copyId}-${it.symbol}-${idx}`}
        onClick={() => handleClick(it.symbol)}
        className={`flex items-center gap-2 px-4 py-1.5 border-r border-white/5 transition-colors ${clickable ? "cursor-pointer hover:bg-white/[0.04]" : ""}`}
        data-testid={copyId === 0 ? `tickertape-${it.symbol}` : undefined}
        title={`${it.stale ? "Cached portfolio price" : (it.quote_source || "Market quote")}${it.quote_at ? ` · ${new Date(it.quote_at).toLocaleString()}` : ""}${clickable ? ` · Open ${it.symbol}` : ""}`}
      >
        <span className="font-mono text-[12px] font-semibold text-white">{it.symbol}</span>
        <span className="text-[9px] text-[var(--text-muted)] uppercase">{it.country}</span>
        <span className="font-mono text-[11px] text-[var(--text-secondary)]">{fmtPrice(it.last_price, it.country)}</span>
        {hasMove ? (
          <span className={`font-mono text-[11px] font-semibold ${up ? "text-emerald-400" : "text-red-400"}`}>
            {up ? "▲" : "▼"} {up ? "+" : ""}{it.day_change_pct.toFixed(2)}%
          </span>
        ) : (
          <span className="font-mono text-[9px] font-semibold tracking-wide text-amber-400/80">CACHED</span>
        )}
      </div>
    );
  };

  // Slower, smoother scroll — ~6s per item, minimum 90s for a full loop
  const animSeconds = Math.max(90, items.length * 6);
  const scrolling = !paused && !hovered;
  const hasLiveQuotes = items.some(item => !item.stale);

  return (
    <div
      className="relative bg-[var(--bg-surface)] border border-white/10 rounded-md overflow-hidden"
      data-testid="ticker-tape"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className="absolute left-0 top-0 bottom-0 z-10 px-2.5 flex items-center gap-1.5 bg-[var(--bg-surface)] border-r border-white/10">
        <Activity size={12} className="text-emerald-400" />
        <span className="text-[10px] tracking-[0.1em] uppercase text-[var(--text-secondary)] font-semibold">
          {hasLiveQuotes ? "Live" : "Cached"}
        </span>
      </div>
      <div className="absolute right-0 top-0 bottom-0 z-10 px-2 flex items-center bg-[var(--bg-surface)] border-l border-white/10">
        <button
          onClick={() => setPaused(p => !p)}
          className="p-1 text-[var(--text-muted)] hover:text-white rounded"
          title={paused ? "Resume" : "Pause"}
          data-testid="ticker-pause-btn"
        >
          {paused ? <Play size={11} /> : <Pause size={11} />}
        </button>
      </div>
      <div className="overflow-hidden mx-[68px]">
        <div
          className="flex whitespace-nowrap"
          style={{
            animation: `tickertape-scroll ${animSeconds}s linear infinite`,
            animationPlayState: scrolling ? "running" : "paused",
            width: "max-content",
          }}
        >
          <div className="flex">{items.map((it, idx) => renderItem(it, idx, 0))}</div>
          <div className="flex" aria-hidden="true">{items.map((it, idx) => renderItem(it, idx, 1))}</div>
        </div>
      </div>
      <style>{`
        @keyframes tickertape-scroll {
          from { transform: translateX(0); }
          to { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  );
}
